/** Search query parser — /query to search; filters: /tickets /visa /tour /mine /agent:name */

export const SEARCH_PAGE_SIZE = 10;

const TYPE_ALIASES = {
  ticket: ['ticket', 'tickets', 'air', 'flight', 'flights', 'tkt'],
  visa: ['visa', 'visas'],
  tour: ['tour', 'tours', 'package', 'packages', 'holiday', 'holidays'],
};

/** Slash tokens that start a free-text search (not added to the query). */
const SLASH_TEXT_TOKENS = new Set(['text', 'find', 'search', 'lookup', 'help', 'q']);

function matchType(token) {
  const key = token.replace(/^\//, '').toLowerCase();
  for (const [type, aliases] of Object.entries(TYPE_ALIASES)) {
    if (aliases.includes(key)) return type;
  }
  return null;
}

function parseSlashToken(part) {
  const inner = part.slice(1).toLowerCase();
  if (inner === 'mine' || inner === 'my' || inner === 'me') {
    return { kind: 'mine' };
  }
  const agentMatch = inner.match(/^(?:agent|by):(.+)$/i);
  if (agentMatch) {
    return { kind: 'agent', value: agentMatch[1].trim() };
  }
  const type = matchType(part);
  if (type) return { kind: 'type', value: type };
  if (SLASH_TEXT_TOKENS.has(inner)) {
    return { kind: 'text_mode' };
  }
  // Bare /name (e.g. /madush) → agent filter; short tokens stay as text search.
  if (/^[a-z][a-z0-9._-]{2,}$/i.test(inner)) {
    return { kind: 'agent', value: part.slice(1) };
  }
  return { kind: 'text', value: part.slice(1) };
}

/**
 * @param {string} raw
 * @returns {{ query: string, type: string|null, agent: string|null, tokens: string[] }}
 */
export function parseAssistantSearchQuery(raw) {
  const parts = String(raw || '').trim().split(/\s+/).filter(Boolean);
  let type = null;
  let agent = null;
  const q = [];

  for (const part of parts) {
    if (part.startsWith('/')) {
      const parsed = parseSlashToken(part);
      if (parsed.kind === 'mine') {
        agent = '__mine__';
        continue;
      }
      if (parsed.kind === 'agent') {
        agent = parsed.value;
        continue;
      }
      if (parsed.kind === 'type') {
        type = parsed.value;
        continue;
      }
      if (parsed.kind === 'text_mode') {
        continue;
      }
      if (parsed.value) q.push(parsed.value);
      continue;
    }
    q.push(part);
  }

  return {
    query: q.join(' ').trim(),
    type,
    agent,
    tokens: parts,
  };
}

export function describeSearchFilters({ type, agent, query }) {
  const bits = [];
  if (type) bits.push(type);
  else bits.push('all services');
  if (agent === '__mine__') bits.push('my leads only');
  else if (agent) bits.push(`agent ~${agent}`);
  else bits.push('all agents');
  if (query) bits.push(`"${query}"`);
  return bits.join(' · ');
}

/**
 * Build /api/leads query params for terminal search.
 * Uses dept_scope=all so /mine returns every inquiry for that agent.
 */
export function buildLeadsSearchParams(parsed, { agentId, page = 1, viewAll = false } = {}) {
  const { query, type, agent } = parsed;
  const params = new URLSearchParams({
    limit: String(SEARCH_PAGE_SIZE),
    page: String(Math.max(1, page)),
    dept_scope: 'all',
  });
  if (query) params.set('q', query);
  if (type) params.set('type', type);
  if (agent && agent !== '__mine__') params.set('agent_q', agent);
  if (agent === '__mine__' && agentId) params.set('agent_id', String(agentId));
  if (viewAll && ((query && query.length >= 2) || (agent && agent !== '__mine__'))) {
    params.set('search_scope', 'global');
  }
  return params;
}

/**
 * Build /leads/ URL for assistant popup — maps terminal search syntax to leads page filters.
 */
export function buildLeadsPageUrl(rawQuery, { agentId, viewAll = false } = {}) {
  const parsed = parseAssistantSearchQuery(rawQuery);
  const p = new URLSearchParams();
  p.set('tab', 'browse');
  p.set('assistant', '1');
  p.set('dept_scope', 'all');
  if (parsed.query) p.set('q', parsed.query);
  if (parsed.type) p.set('type', parsed.type);
  if (parsed.agent === '__mine__' && agentId) {
    p.set('agent_id', String(agentId));
  } else if (parsed.agent) {
    p.set('agent_q', parsed.agent);
  }
  if (viewAll && ((parsed.query && parsed.query.length >= 2) || parsed.agent)) {
    p.set('search_scope', 'global');
  }
  return `/leads/?${p.toString()}`;
}

export function searchPageLabel(page, totalPages, total) {
  const p = Math.max(1, page);
  const tp = Math.max(1, totalPages);
  const from = total ? (p - 1) * SEARCH_PAGE_SIZE + 1 : 0;
  const to = total ? Math.min(p * SEARCH_PAGE_SIZE, total) : 0;
  if (tp <= 1) {
    return total ? `${total} ${total === 1 ? 'result' : 'results'}` : 'No results';
  }
  return `page ${p} of ${tp}${total ? ` · records ${from}–${to} of ${total}` : ''}`;
}

/**
 * Fetch paginated leads for terminal / GUI search.
 * @returns {Promise<{ results: object[], parsed: object, total: number, totalPages: number, page: number, error?: string }>}
 */
export async function fetchAssistantSearchResults(rawQuery, page, {
  api,
  agentId,
  viewAll = false,
  normalizePhone,
  classifySearchQuery,
} = {}) {
  const parsed = parseAssistantSearchQuery(rawQuery);
  const { query } = parsed;
  const seen = new Map();

  function add(item) {
    if (!item?.id) return;
    if (!seen.has(item.id)) seen.set(item.id, item);
  }

  const params = buildLeadsSearchParams(parsed, {
    agentId,
    page,
    viewAll,
  });

  let total = 0;
  let totalPages = 1;
  let results = [];

  try {
    const list = await api(`/api/leads?${params}`);
    results = list.leads || [];
    results.forEach(add);
    total = list.total ?? results.length;
    totalPages = list.total_pages ?? Math.max(1, Math.ceil(total / SEARCH_PAGE_SIZE));
  } catch (e) {
    return { results: [], parsed, total: 0, totalPages: 1, page, error: e.message };
  }

  if (page === 1 && query && normalizePhone && classifySearchQuery) {
    const { kind, query: q } = classifySearchQuery(query);
    if (kind === 'phone' || kind === 'passport') {
      try {
        const dupParams = new URLSearchParams({ limit: String(SEARCH_PAGE_SIZE), dept_scope: 'all' });
        if (kind === 'phone') dupParams.set('phone', normalizePhone(q));
        if (kind === 'passport') dupParams.set('passport', q);
        const dup = await api(`/api/check-duplicates?${dupParams}`);
        (dup.duplicates || []).forEach(add);
      } catch { /* ignore */ }
    }
  }

  results = [...seen.values()];
  if (page === 1 && results.length > SEARCH_PAGE_SIZE) {
    results = results.slice(0, SEARCH_PAGE_SIZE);
  }

  return { results, parsed, total, totalPages, page };
}
